import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { AIProvider } from "@/lib/ai";
import { AIConsentModal } from "@/components/AIConsentModal";
import Login from "@/pages/Login";
import ConfirmArrival from "@/pages/ConfirmArrival";
import CustomerAccept from "@/pages/CustomerAccept";
import PublicChat from "@/pages/PublicChat";
import DeleteAccountRequest from "@/pages/public/DeleteAccountRequest";
import VendorLayout from "@/components/VendorLayout";
import ManagementLayout from "@/components/ManagementLayout";
import VendorDashboard from "@/pages/vendor/VendorDashboard";
import VendorProfile from "@/pages/vendor/VendorProfile";
import IncomingRequests from "@/pages/vendor/IncomingRequests";
import ActiveJobs from "@/pages/vendor/ActiveJobs";
import History from "@/pages/vendor/History";
import Notifications from "@/pages/vendor/Notifications";
import VendorSettings from "@/pages/vendor/Settings";
import ExpensesPage from "@/pages/vendor/ExpensesPage";
import TechSchedule from "@/pages/vendor/TechSchedule";
import OwnerDashboard from "@/pages/management/OwnerDashboard";
import VendorDirectory from "@/pages/management/VendorDirectory";
import RequestMonitoring from "@/pages/management/RequestMonitoring";
import ApprovalCenter from "@/pages/management/ApprovalCenter";
import AuditLogs from "@/pages/management/AuditLogs";
import UserManagement from "@/pages/management/UserManagement";
import Locations from "@/pages/management/Locations";
import TechnicianMap from "@/pages/management/TechnicianMap";
import FinancialDashboard from "@/pages/management/FinancialDashboard";
import Vehicles from "@/pages/management/Vehicles";
import XactimateSettings from "@/pages/management/XactimateSettings";
import RatingsDashboard from "@/pages/management/RatingsDashboard";
import Leaderboard from "@/pages/management/Leaderboard";
import DrivingSafety from "@/pages/management/DrivingSafety";
import DrivingSettings from "@/pages/management/DrivingSettings";
import SpecialPermissions from "@/pages/management/SpecialPermissions";
import MoistureEquipment from "@/pages/management/MoistureEquipment";
import ContentGuardAudit from "@/pages/management/ContentGuardAudit";
import MyBelongings from "@/pages/MyBelongings";
import Customers from "@/pages/management/Customers";
import InspectionWizard from "@/pages/vendor/inspection/InspectionWizard";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-zinc-500 font-body">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "vendor") return <Navigate to="/vendor" replace />;
    return <Navigate to="/management" replace />;
  }
  return children;
}

function RoleRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "vendor") return <Navigate to="/vendor" replace />;
  return <Navigate to="/management" replace />;
}

function App() {
  return (
    <AuthProvider>
      <AIProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/confirm-arrival/:requestId" element={<ConfirmArrival />} />
          <Route path="/accept-job/:requestId" element={<CustomerAccept />} />
          <Route path="/chat/:requestId" element={<PublicChat />} />
          <Route path="/fieldproiq/delete-account" element={<DeleteAccountRequest />} />
          <Route path="/my-belongings/:requestId" element={<MyBelongings />} />
          <Route path="/my-belongings/token/:token" element={<MyBelongings />} />
          <Route path="/" element={<RoleRouter />} />

          {/* Vendor/Technician Routes */}
          <Route path="/vendor" element={
            <ProtectedRoute allowedRoles={["vendor"]}>
              <VendorLayout />
            </ProtectedRoute>
          }>
            <Route index element={<VendorDashboard />} />
            <Route path="profile" element={<VendorProfile />} />
            <Route path="requests" element={<IncomingRequests />} />
            <Route path="active" element={<ActiveJobs />} />
            <Route path="history" element={<History />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="schedule" element={<TechSchedule />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<VendorSettings />} />
          </Route>

          {/* Inspection Wizard — full-screen (outside VendorLayout to hide bottom nav) */}
          <Route path="/vendor/inspection/:requestId" element={
            <ProtectedRoute allowedRoles={["vendor"]}>
              <InspectionWizard />
            </ProtectedRoute>
          } />

          {/* Management Routes */}
          <Route path="/management" element={
            <ProtectedRoute allowedRoles={["dispatcher", "manager", "owner", "admin", "branch_manager"]}>
              <ManagementLayout />
            </ProtectedRoute>
          }>
            <Route index element={<OwnerDashboard />} />
            <Route path="technicians" element={<VendorDirectory />} />
            <Route path="requests" element={<RequestMonitoring />} />
            <Route path="approvals" element={<ApprovalCenter />} />
            <Route path="locations" element={<Locations />} />
            <Route path="map" element={<TechnicianMap />} />
            <Route path="financials" element={<FinancialDashboard />} />
            <Route path="vehicles" element={<Vehicles />} />
            <Route path="xactimate" element={<XactimateSettings />} />
            <Route path="ratings" element={<RatingsDashboard />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="driving" element={<DrivingSafety />} />
            <Route path="driving/settings" element={<DrivingSettings />} />
            <Route path="audit" element={<AuditLogs />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="permissions" element={<SpecialPermissions />} />
            <Route path="moisture-equipment" element={<MoistureEquipment />} />
            <Route path="contentguard" element={<ContentGuardAudit />} />
            <Route path="customers" element={<Customers />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <AIConsentModal />
      <Toaster position="top-right" richColors />
      </AIProvider>
    </AuthProvider>
  );
}

export default App;
